import { LOGO, BRAND_NAME } from "@/lib/brand";

type BrandLogoSize = "xs" | "sm" | "md" | "lg" | "xl";

const sizeConfig: Record<
  BrandLogoSize,
  { img: string; text: string; width: number; height: number }
> = {
  xs: { img: "h-5 w-5", text: "text-sm", width: 20, height: 20 },
  sm: { img: "h-6 w-6", text: "text-base", width: 24, height: 24 },
  md: { img: "h-7 w-7", text: "text-lg", width: 28, height: 28 },
  lg: { img: "h-9 w-9", text: "text-xl", width: 36, height: 36 },
  xl: { img: "h-16 w-16", text: "text-4xl", width: 64, height: 64 },
};

interface BrandLogoProps {
  showText?: boolean;
  size?: BrandLogoSize;
  className?: string;
  textClassName?: string;
  priority?: boolean;
  trademark?: boolean;
  /** Use "dark" on always-dark surfaces (e.g. footer). Default follows theme. */
  variant?: "auto" | "light" | "dark";
}

export function BrandLogo({
  showText = true,
  size = "md",
  className = "",
  textClassName = "",
  priority = false,
  trademark = false,
  variant = "auto",
}: BrandLogoProps) {
  const config = sizeConfig[size];
  const imgClass = `${config.img} object-contain shrink-0`;

  const lightImgClass =
    variant === "dark"
      ? `${imgClass} hidden`
      : variant === "light"
        ? imgClass
        : `${imgClass} dark:hidden`;

  const darkImgClass =
    variant === "dark"
      ? imgClass
      : variant === "light"
        ? `${imgClass} hidden`
        : `${imgClass} hidden dark:block`;

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={LOGO.withoutBg}
        alt={BRAND_NAME}
        className={lightImgClass}
        width={config.width}
        height={config.height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
      />
      <img
        src={LOGO.dark}
        alt=""
        aria-hidden="true"
        className={darkImgClass}
        width={config.width}
        height={config.height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
      />
      {showText && (
        <span
          className={`font-bold tracking-tight ${config.text} ${textClassName}`}
        >
          {BRAND_NAME}
          {trademark && "™"}
        </span>
      )}
    </span>
  );
}
